import React from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Read-only label/value list for detail cards.
 * Rows render as a labelled definition list with subtle dividers; a row with
 * empty content renders nothing, so callers can list every field unconditionally.
 */
export function DetailList({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return <dl className={cn('divide-y divide-border/60', className)}>{children}</dl>
}

export interface DetailRowProps {
  label: string
  icon?: LucideIcon
  children?: React.ReactNode
  /** Overrides the row layout — e.g. a wider label column for long labels. */
  className?: string
}

function isEmpty(value: React.ReactNode): boolean {
  if (value == null || value === false || value === '') return true
  return Array.isArray(value) && value.length === 0
}

export function DetailRow({ label, icon: Icon, children, className }: DetailRowProps) {
  if (isEmpty(children)) return null
  return (
    <div className={cn('grid grid-cols-[8rem_1fr] items-baseline gap-4 py-2.5 first:pt-0 last:pb-0', className)}>
      <dt className="flex items-center gap-1.5 text-sm text-muted-foreground">
        {Icon && <Icon className="size-3.5 shrink-0" />}
        <span className="truncate" title={label}>{label}</span>
      </dt>
      <dd className="min-w-0 text-sm break-words">{children}</dd>
    </div>
  )
}
